/**
 * AIOfferAccept — pupil-facing page at /ai-offer-accept (Phase C,
 * Feb 13, 2026).
 *
 * Three modes, picked from query string:
 *   1. ?offer_id=<id>                       → review + Accept screen.
 *   2. ?offer_id=<id>&session_id=<sid>      → returning from Stripe.
 *                                             Poll /checkout-status
 *                                             until paid → route to
 *                                             /training?intent=…
 *                                             &ai_offer_id=…
 *   3. ?offer_id=<id>&cancelled=1           → Stripe cancel return.
 *                                             Shows calm copy with a
 *                                             "Try again" button.
 *
 * Calm framing throughout — no scare tactics, no upsells, no
 * subscriptions, no telemetry.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Sparkles, ShieldCheck, CreditCard, ArrowRight, X,
  CheckCircle, AlertTriangle,
} from "lucide-react";
import { MEDIA_FEATURE_ENABLED } from "../lib/mediaFeatureFlag";

const API = process.env.REACT_APP_BACKEND_URL;


function formatPrice(pence) {
  const n = (pence || 0) / 100;
  return `£${n.toFixed(2)}`;
}


export default function AIOfferAccept() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const offerId = params.get("offer_id");
  const sessionId = params.get("session_id");
  const cancelled = params.get("cancelled") === "1";

  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [polling, setPolling] = useState(false);

  const loadOffer = useCallback(async () => {
    if (!MEDIA_FEATURE_ENABLED) {
      // Media quarantine — page is also wrapped in <QuarantinedRoute>;
      // this is the doctrine pin a future grep will land on.
      setLoading(false);
      setError("quarantined");
      return;
    }
    if (!offerId) {
      setError("This link is missing the offer id.");
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/ai-offers/${offerId}`, {
        withCredentials: true,
      });
      setOffer(r.data);
    } catch (e) {
      setError(
        e?.response?.data?.detail
          || "Could not load this offer.",
      );
    } finally {
      setLoading(false);
    }
  }, [offerId]);
  useEffect(() => { loadOffer(); }, [loadOffer]);

  // ── Returning-from-Stripe poll loop ─────────────────────────────
  useEffect(() => {
    if (!sessionId || !offerId) return;
    let cancelledFlag = false;
    let attempts = 0;
    setPolling(true);
    const poll = async () => {
      while (!cancelledFlag) {
        attempts += 1;
        try {
          const r = await axios.get(
            `${API}/api/ai-offers/checkout-status/${sessionId}`,
            { withCredentials: true },
          );
          const data = r.data;
          if (data?.status === "paid") {
            const intent = data?.offer?.review_intent || "general_drive";
            nav(
              `/training?intent=${encodeURIComponent(intent)}`
              + `&ai_offer_id=${offerId}`,
              { replace: true },
            );
            return;
          }
        } catch {
          // Tolerate a transient error and keep polling. After
          // ~20 attempts (≈40s) we stop politely so the pupil
          // isn't stuck on a spinner forever.
        }
        if (attempts >= 20) {
          setPolling(false);
          toast.error(
            "We couldn't confirm your payment automatically. Refresh "
            + "the page in a moment, or check your inbox.",
          );
          return;
        }
        await new Promise((r) => setTimeout(r, 2000));
      }
    };
    poll();
    return () => { cancelledFlag = true; };
  }, [sessionId, offerId, nav]);

  // ── Accept handler — calls /accept → redirect to Stripe ────────
  const handleAccept = async () => {
    if (!offer) return;
    setAccepting(true);
    try {
      const origin = window.location.origin;
      const r = await axios.post(
        `${API}/api/ai-offers/${offer.id}/accept`,
        { origin_url: origin },
        { withCredentials: true },
      );
      const url = r.data?.url;
      if (url) {
        window.location.href = url;
        return;
      }
      // Already-accepted edge case — refresh state.
      await loadOffer();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail
          || "Could not start payment. Please try again.",
      );
    } finally {
      setAccepting(false);
    }
  };

  // ── Render branches ────────────────────────────────────────────

  if (loading) {
    return (
      <Shell>
        <Centered>
          <Loader2 className="w-5 h-5 text-indigo-300 animate-spin" />
        </Centered>
      </Shell>
    );
  }

  if (error || !offer) {
    return (
      <Shell>
        <ErrorCard
          message={error || "Offer not found."}
          onBack={() => nav("/learn")}
        />
      </Shell>
    );
  }

  // Returning from Stripe — show the polling screen while we
  // confirm. We do not show any payment UI here because the pupil
  // already completed Stripe Checkout.
  if (sessionId) {
    return (
      <Shell>
        <div
          data-testid="ai-offer-accept-polling"
          className="rounded-2xl border border-indigo-700/40 bg-slate-900/60 p-6 space-y-4 text-center"
        >
          <Loader2 className="w-6 h-6 text-indigo-300 animate-spin mx-auto" />
          <p className="text-base text-white font-extrabold">
            Confirming your payment…
          </p>
          <p className="text-[12px] text-slate-300 leading-relaxed">
            This usually takes a few seconds. We'll take you to the
            recording guide as soon as Stripe confirms it.
          </p>
          {!polling && (
            <button
              type="button"
              onClick={loadOffer}
              className="text-[12px] text-indigo-300 underline underline-offset-2 font-extrabold"
            >
              Try again
            </button>
          )}
        </div>
      </Shell>
    );
  }

  if (cancelled) {
    return (
      <Shell>
        <div
          data-testid="ai-offer-accept-cancelled"
          className="rounded-2xl border border-amber-700/40 bg-amber-900/15 p-5 space-y-3"
        >
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-300" />
            <p className="text-[11px] uppercase tracking-widest font-extrabold text-amber-300">
              Payment cancelled
            </p>
          </div>
          <p className="text-[13.5px] text-amber-100 leading-relaxed">
            No charge was made. You can try again whenever you're ready.
          </p>
          <button
            type="button"
            data-testid="ai-offer-accept-retry"
            onClick={handleAccept}
            disabled={accepting}
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-500 hover:bg-indigo-400 disabled:opacity-60 text-white text-sm font-extrabold py-2.5 active:scale-[0.99] transition"
          >
            {accepting
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <CreditCard className="w-4 h-4" />}
            Try payment again
          </button>
        </div>
      </Shell>
    );
  }

  // Terminal states — calm copy
  if (
    offer.status === "declined"
    || offer.status === "cancelled"
    || offer.status === "expired"
  ) {
    return (
      <Shell>
        <ClosedCard offer={offer} onBack={() => nav("/learn")} />
      </Shell>
    );
  }

  if (offer.status === "paid") {
    return (
      <Shell>
        <PaidCard
          offer={offer}
          onContinue={() => nav(
            `/training?intent=${encodeURIComponent(offer.review_intent)}`
            + `&ai_offer_id=${offer.id}`,
            { replace: true },
          )}
        />
      </Shell>
    );
  }

  // status === "pending" or "accepted" (pupil can re-accept)
  return (
    <Shell>
      <PendingAcceptCard
        offer={offer}
        accepting={accepting}
        onAccept={handleAccept}
        onBack={() => nav("/learn")}
      />
    </Shell>
  );
}


// ── UI primitives ────────────────────────────────────────────────


function Shell({ children }) {
  return (
    <div
      data-testid="ai-offer-accept-page"
      className="min-h-dvh no-overflow-x bg-slate-950 text-white"
    >
      <div className="max-w-lg mx-auto px-4 py-6 space-y-4">
        {children}
      </div>
    </div>
  );
}


function Centered({ children }) {
  return (
    <div className="min-h-[40dvh] flex items-center justify-center">
      {children}
    </div>
  );
}


function ErrorCard({ message, onBack }) {
  return (
    <div
      data-testid="ai-offer-accept-error"
      className="rounded-2xl border border-rose-700/40 bg-rose-900/15 p-5 space-y-3"
    >
      <p className="text-[13.5px] text-rose-100 leading-relaxed">{message}</p>
      <button
        type="button"
        onClick={onBack}
        className="w-full rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-100 text-sm font-extrabold py-2.5"
      >
        Back to home
      </button>
    </div>
  );
}


function ClosedCard({ offer, onBack }) {
  const label = ({
    declined: "You declined this offer",
    cancelled: "Your instructor cancelled this offer",
    expired: "This offer has expired",
  })[offer.status] || "Offer closed";
  return (
    <div
      data-testid="ai-offer-accept-closed"
      className="rounded-2xl border border-slate-700/60 bg-slate-900/60 p-5 space-y-3"
    >
      <div className="flex items-center gap-2">
        <X className="w-4 h-4 text-slate-400" />
        <p className="text-[11px] uppercase tracking-widest font-extrabold text-slate-400">
          {label}
        </p>
      </div>
      <p className="text-[13.5px] text-slate-200 leading-relaxed">
        No payment was taken. If you'd like another AI assessment,
        message your instructor and they can send a fresh one.
      </p>
      <button
        type="button"
        onClick={onBack}
        className="w-full rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-100 text-sm font-extrabold py-2.5"
      >
        Back to home
      </button>
    </div>
  );
}


function PaidCard({ offer, onContinue }) {
  return (
    <div
      data-testid="ai-offer-accept-paid"
      className="rounded-2xl border border-emerald-700/40 bg-emerald-900/15 p-5 space-y-3"
    >
      <div className="flex items-center gap-2">
        <CheckCircle className="w-4 h-4 text-emerald-300" />
        <p className="text-[11px] uppercase tracking-widest font-extrabold text-emerald-300">
          Paid — ready to record
        </p>
      </div>
      <p className="text-[13.5px] text-emerald-100 leading-relaxed">
        Your <span className="font-extrabold">{offer.review_intent_label}</span>{" "}
        is unlocked. We'll guide you through recording your drive.
      </p>
      <button
        type="button"
        data-testid="ai-offer-accept-continue-to-record"
        onClick={onContinue}
        className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-900 text-sm font-extrabold py-2.5 active:scale-[0.99] transition"
      >
        Continue to recording <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}


function PendingAcceptCard({ offer, accepting, onAccept, onBack }) {
  const total = offer.retail_price_pence;
  return (
    <div
      data-testid="ai-offer-accept-pending"
      className="rounded-2xl border border-indigo-700/40 bg-slate-900/60 p-5 space-y-4"
    >
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-indigo-300" />
          <p className="text-[10px] uppercase tracking-widest font-extrabold text-indigo-300">
            AI assessment offer
          </p>
        </div>
        <h1
          data-testid="ai-offer-accept-label"
          className="text-xl font-extrabold text-white leading-tight"
        >
          {offer.review_intent_label}
        </h1>
        <p className="text-[12px] text-slate-400">
          from {offer.instructor_name}
        </p>
      </div>

      {offer.note && (
        <p
          data-testid="ai-offer-accept-note"
          className="text-[12px] text-slate-300 italic leading-relaxed bg-slate-800/50 rounded-lg p-3 border border-slate-700/60"
        >
          "{offer.note}"
        </p>
      )}

      <div className="rounded-xl border border-slate-700/60 bg-slate-900/80 p-3 space-y-2">
        <div className="flex justify-between text-[13px]">
          <span className="text-slate-300">Total</span>
          <span
            data-testid="ai-offer-accept-total"
            className="text-white font-extrabold"
          >
            {formatPrice(total)}
          </span>
        </div>
        <p className="text-[11px] text-slate-400 leading-relaxed">
          Secure payment via Stripe. You pay {offer.instructor_name}{" "}
          directly — the platform fee is included.
        </p>
      </div>

      <div className="rounded-xl border border-slate-700/60 bg-slate-900/60 p-3 space-y-2">
        <p className="text-[10px] uppercase tracking-widest font-extrabold text-slate-400">
          What happens next
        </p>
        <ol className="space-y-1.5 text-[12px] text-slate-200 list-decimal list-inside leading-relaxed">
          <li>Pay securely with Stripe.</li>
          <li>Record your drive — we'll guide you through phone-mount.</li>
          <li>AI analyses your recording.</li>
          <li>Your instructor reviews the result with you.</li>
        </ol>
      </div>

      <button
        type="button"
        data-testid="ai-offer-accept-pay-button"
        onClick={onAccept}
        disabled={accepting}
        className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-500 hover:bg-indigo-400 disabled:opacity-60 text-white text-sm font-extrabold py-3 active:scale-[0.99] transition"
      >
        {accepting
          ? <Loader2 className="w-4 h-4 animate-spin" />
          : <ShieldCheck className="w-4 h-4" />}
        Pay {formatPrice(total)} & continue
      </button>

      <button
        type="button"
        data-testid="ai-offer-accept-back"
        onClick={onBack}
        className="w-full text-[12px] text-slate-400 hover:text-slate-200 font-extrabold py-1"
      >
        Maybe later
      </button>
    </div>
  );
}
